/**
 * Supplier portal accounts.
 *
 * Thin wrapper over /compuzz/rest/v2/portalUsers. These are not Userpids: a
 * portal account only ever opens the supplier portal, never the dashboard, so it
 * has its own endpoints and its own lifecycle (invited → active → disabled).
 */
define('entities/suppliers/portal_users',[
  'app',
  'jquery',
  'underscore',
  'settings'
], function (
  App,
  $,
  _,
  Settings
) {
  'use strict';

  var API = {

    getUsers: function (supplierSecId) {
      return $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId)
      });
    },

    /**
     * Creates the account and returns its first invitation link. The link is
     * returned rather than emailed — the operator sends it through whatever
     * channel they already use with that supplier.
     */
    createUser: function (data) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers'),
        contentType: 'application/json',
        data: JSON.stringify(data)
      });
    },

    /** A fresh link. Also the "supplier forgot their password" path. */
    inviteUser: function (userSecId) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/' + userSecId + '/invite')
      });
    },

    disableUser: function (userSecId) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/' + userSecId + '/disable')
      });
    },

    enableUser: function (userSecId) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/' + userSecId + '/enable')
      });
    },

    /** Whether this account gets an email for each new order on the portal. */
    setNotifyNewOrders: function (userSecId, enabled) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/' + userSecId + '/notify-new-orders', {enabled: enabled === true})
      });
    },

    unlockUser: function (userSecId) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/' + userSecId + '/unlock')
      });
    },

    getRouting: function (supplierSecId) {
      return $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId + '/routing')
      });
    },

    /**
     * Turns portal routing on or off for one supplier. The endpoint writes a
     * single flag on the supplier's config — it never touches the API
     * credentials stored alongside it.
     */
    setRouting: function (supplierSecId, enabled) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId + '/routing'),
        contentType: 'application/json',
        data: JSON.stringify({enabled: enabled})
      });
    },

    /**
     * Which sections of the portal this supplier may open: orders, products,
     * accounts. Separate from routing — one says where an order is delivered,
     * the other what the supplier may see once signed in.
     */
    /**
     * Where an order goes in Compuzz when this supplier accepts or ships it.
     * Blank codes clear the mapping: the order then stays where it is.
     */
    setStatusMapping: function (supplierSecId, mapping) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId + '/statusMapping'),
        contentType: 'application/json',
        data: JSON.stringify(mapping)
      });
    },

    setFeatures: function (supplierSecId, features) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId + '/features'),
        contentType: 'application/json',
        data: JSON.stringify(features)
      });
    },

    /**
     * What this supplier still has on their bench: every order that is not
     * rejected, shipped or cancelled.
     */
    getOrders: function (supplierSecId, scope, start, pageSize) {
      return $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId + '/orders',
          {scope: scope || 'open', start: start || 0, pageSize: pageSize || 15})
      });
    },

    /**
     * Every supplier with something going on in the portal, in one call, so the
     * screen does not have to be opened supplier by supplier to find them.
     */
    getOverview: function (all) {
      return $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/portalUsers/overview', {all: all === true})
      });
    },

    /**
     * Whether this supplier may buy carrier labels on our Sendcloud account, and
     * the two ids a parcel needs. Separate from routing: one says where the order
     * goes, the other says whether they may spend our carrier budget.
     */
    setLabelSettings: function (supplierSecId, settings) {
      return $.ajax({
        type: 'POST',
        url: Settings.url('compuzz2', 'v2/portalUsers/supplier/' + supplierSecId + '/labels'),
        contentType: 'application/json',
        data: JSON.stringify(settings)
      });
    },

    /**
     * The suppliers this account buys from, as shown in the price factors tab:
     * accounts shared *from* someone else to us.
     */
    getSuppliers: function () {
      var defer = $.Deferred();
      var request = App.request('caccounts:get-share', {
        cAccountId: Settings.get('currentCAccount').secId,
        share: 'from'
      });
      $.when(request).done(function (list) {
        var suppliers = [];
        _.each(list, function (item) {
          if (item.caccountFrom) {
            suppliers.push(item.caccountFrom);
          }
        });
        defer.resolve(_.sortBy(suppliers, 'code'));
      }).fail(function () {
        defer.resolve([]);
      });
      return defer.promise();
    }
  };

  App.reqres.setHandler('portal-users:get', function (supplierSecId) {
    return API.getUsers(supplierSecId);
  });

  App.reqres.setHandler('portal-users:create', function (data) {
    return API.createUser(data);
  });

  App.reqres.setHandler('portal-users:invite', function (userSecId) {
    return API.inviteUser(userSecId);
  });

  App.reqres.setHandler('portal-users:disable', function (userSecId) {
    return API.disableUser(userSecId);
  });

  App.reqres.setHandler('portal-users:enable', function (userSecId) {
    return API.enableUser(userSecId);
  });

  App.reqres.setHandler('portal-users:set-notify-new-orders', function (userSecId, enabled) {
    return API.setNotifyNewOrders(userSecId, enabled);
  });

  App.reqres.setHandler('portal-users:unlock', function (userSecId) {
    return API.unlockUser(userSecId);
  });

  App.reqres.setHandler('portal-users:suppliers', function () {
    return API.getSuppliers();
  });

  App.reqres.setHandler('portal-users:routing', function (supplierSecId) {
    return API.getRouting(supplierSecId);
  });

  App.reqres.setHandler('portal-users:set-routing', function (supplierSecId, enabled) {
    return API.setRouting(supplierSecId, enabled);
  });

  App.reqres.setHandler('portal-users:set-label-settings', function (supplierSecId, settings) {
    return API.setLabelSettings(supplierSecId, settings);
  });

  App.reqres.setHandler('portal-users:set-status-mapping', function (supplierSecId, mapping) {
    return API.setStatusMapping(supplierSecId, mapping);
  });

  App.reqres.setHandler('portal-users:set-features', function (supplierSecId, features) {
    return API.setFeatures(supplierSecId, features);
  });

  App.reqres.setHandler('portal-users:overview', function (all) {
    return API.getOverview(all);
  });

  App.reqres.setHandler('portal-users:orders', function (supplierSecId, scope, start, pageSize) {
    return API.getOrders(supplierSecId, scope, start, pageSize);
  });
});

